import {
  Divider,
  DropdownButton,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import {
  DashboardLayout,
  QueueDashboardQueueSortBy,
} from 'lib-dashboard-types';
import { ampli } from 'lib-shared-tracking';
import { Fragment, memo, useMemo, useState } from 'react';

import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';

import styles from './queueDashboardPreferencesControl.module.scss';
import {
  layoutPreferenceAssets,
  sortPreferenceAssets,
} from './utils/assets/dashboardPreferencesAssets';

export const QueueDashboardPreferencesControl = memo(
  function QueueDashboardPreferencesControl(): JSX.Element {
    const { params, patchParams } = useAppSearchParams('/dashboard');
    const storage = useServerStorage('user');

    const currentLayout = storage.data.dashboard?.layout ?? 'default';
    const [isOpen, setIsOpen] = useState(false);

    const handleLayoutChange = (layout: DashboardLayout) => {
      storage.set({
        dashboard: (draft) => {
          draft.layout = layout;
        },
      });

      ampli.dashboardLayoutChanged({ layout });
    };

    const handleSortByChange = (value: QueueDashboardQueueSortBy) => {
      patchParams({
        set: {
          sort_by: value,
        },
        unset: ['limit', 'offset'],
      });

      ampli.dashboardQueuesSortingUpdated({ type: value });
    };

    const selectedValues: [QueueDashboardQueueSortBy, DashboardLayout] =
      useMemo(
        () => [params.sort_by ?? 'name:asc', currentLayout],
        [currentLayout, params.sort_by],
      );

    return (
      <DropdownButton
        show={isOpen}
        onToggle={setIsOpen}
        icon="preferences"
        buttonProps={{
          variant: 'secondary',
          disabled: !!params.q,
        }}
        fixed
        dropdown={
          <>
            <SelectionList
              className={styles.list}
              isActive={false}
              selectedValues={selectedValues}
              defaultActiveItemIndex={0}
            >
              <Divider padding="300 300 200">
                <Text.Paragraph as="span" color="tertiary">
                  Sort by
                </Text.Paragraph>
              </Divider>

              {sortPreferenceAssets.map(({ value, label, image }) => (
                <Fragment key={value}>
                  <SelectionList.Item
                    value={value}
                    contentClassName={styles.item}
                    onClick={() => handleSortByChange(value)}
                  >
                    {label} {image}
                  </SelectionList.Item>
                </Fragment>
              ))}

              <Divider padding="200 300">
                <Text.Paragraph as="span" color="tertiary">
                  Layout
                </Text.Paragraph>
              </Divider>

              {layoutPreferenceAssets.map(({ value, label, image }) => (
                <Fragment key={value}>
                  <SelectionList.Item
                    value={value}
                    contentClassName={styles.item}
                    onClick={() => handleLayoutChange(value)}
                  >
                    {label} {image}
                  </SelectionList.Item>
                </Fragment>
              ))}
            </SelectionList>
          </>
        }
      />
    );
  },
);
